import { find } from "lodash";
import { loadStripe } from "@stripe/stripe-js";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Button,
  Alert,
  AlertTitle,
  TextField,
  useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ArrowBack } from "@mui/icons-material";

import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import { fillParent, scrollingBox, footerFlex } from "utils/base-styles";
import {
  useSubscriptions,
  IndividualPricing,
  useUserData,
} from "features/User";

import Footer from "components/layout/Footer";
import PageTitle from "components/layout/PageTitle";
import { FeatureHelpVideo } from "features/Support";
import { Helmet } from "react-helmet-async";

let stripePromise = null;

export default function IndividualSignUpPage() {
  const { shape } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const isMobile = useIsMobile();
  const { groupId, groupRole, referralCode, availableCredit } = useUserData();
  console.log("referralCode", referralCode);

  const {
    checkPromoCode,
    coupons,
    createCheckoutSession,
    expireCheckoutSession,
    getIndividualPlanBasePrice,
    products,
    subsLoading,
  } = useSubscriptions();

  const [gradeLevel, setGradeLevel] = useState("k12");
  const [planLength, setPlanLength] = useState("yearly");
  const [basePrice, setBasePrice] = useState(
    getIndividualPlanBasePrice(gradeLevel, planLength)
  );
  const [autoRenew] = useState(true);
  const [activeProduct, setActiveProduct] = useState(null);
  const [checkoutSession, setCheckoutSession] = useState(null);
  const [activeCoupon, setActiveCoupon] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState(null);
  const [isReferralCode, setIsReferralCode] = useState(false);

  function handleSelectionChange(value, setFn) {
    setFn(value);
  }

  async function handleProductSelectionContinue() {
    if (activeProduct == null) {
      return;
    }
    setLoading(true);
    try {
      const session = await createCheckoutSession(
        activeProduct.id,
        activeCoupon != null ? activeCoupon.id : "",
        autoRenew ? "subscription" : "payment"
      );
      setCheckoutSession(session);
    } catch (error) {
      toast.error("Oops! Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleProductSelectionBack() {
    setLoading(true);
    try {
      const success = await expireCheckoutSession(checkoutSession.id);
      if (success) {
        setCheckoutSession(null);
      } else {
        toast.error("Oops! Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Oops! Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleClearCoupon() {
    setActiveCoupon(null);
    setPromoError(null);
    setPromoCode(referralCode || "");
    setIsReferralCode(false);
  }

  async function handleApplyCoupon() {
    if (promoCode.trim() === "") {
      setPromoError("Must enter a promo code.");
      return;
    }

    try {
      const coupon = await checkPromoCode(
        activeProduct.id,
        promoCode.trim().toUpperCase()
      );
      // const coupon = find(coupons, (coupon) => {
      //   return find(coupon.promoCodes, { code: promoCode }) != null;
      // });
      if (coupon != null) {
        setActiveCoupon(coupon);
        setPromoError(null);
      } else {
        setPromoError("Not a valid promo code.");
      }
    } catch (error) {
      let msg = "Not a valid promo code.";
      if (error?.response?.data) {
        if (typeof error.response.data === "string") {
          msg = error.response.data;
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        }
      }
      setPromoError(msg);
      setActiveCoupon(null);
      setIsReferralCode(false);
    }
  }

  function handleEnterKey(event, callback) {
    if (event.key === "Enter") {
      callback();
    }
  }

  useEffect(() => {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);
    return () => {
      stripePromise = null;
    };
  }, []);

  useEffect(() => {
    setBasePrice(getIndividualPlanBasePrice(gradeLevel, planLength));
    const planType = `individual-${gradeLevel}-${planLength}-${autoRenew ? "sub" : "nosub"
      }`;
    const activeProduct = find(
      products,
      (product) =>
        product.metadata.planType &&
        product.metadata.planType.toLowerCase() === planType.toLowerCase()
    );
    setActiveProduct(activeProduct);
  }, [gradeLevel, planLength, autoRenew, products, getIndividualPlanBasePrice]);

  useEffect(() => {
    async function findDefaultCouponOrReferral() {
      if (activeProduct == null) {
        return;
      }

      if (referralCode && planLength === "yearly") {
        const checkedCoupon = await checkPromoCode(
          activeProduct.id,
          referralCode
        );
        if (checkedCoupon != null) {
          const matchedCode = find(
            checkedCoupon.promoCodes,
            (promo) => promo.code === referralCode.toUpperCase()
          );
          setPromoCode(matchedCode ? matchedCode.code : "");
          setIsReferralCode(true);
          setActiveCoupon(checkedCoupon);
        } else {
          setActiveCoupon(null);
          setIsReferralCode(false);
          setPromoCode("");
        }
        return;
      }

      const defaultCoupon = find(coupons, (coupon) =>
        coupon.isForProduct(activeProduct.id)
      );
      if (defaultCoupon != null) {
        const defaultCode = find(
          defaultCoupon.promoCodes,
          (code) => code.isDefault
        );
        if (defaultCode) {
          const checkedCoupon = await checkPromoCode(
            activeProduct.id,
            defaultCode.code
          );
          if (checkedCoupon != null) {
            setPromoCode(defaultCode.code);
            setActiveCoupon(checkedCoupon);
            setIsReferralCode(false);
          } else {
            setActiveCoupon(null);
            setPromoCode("");
            setIsReferralCode(false);
          }
        }
      } else {
        setActiveCoupon(null);
        setPromoCode("");
        setIsReferralCode(false);
      }
    }
    findDefaultCouponOrReferral();
  }, [coupons, activeProduct, planLength, referralCode]);

  useEffect(() => {
    if (groupId != null) {
      if (groupRole === "OrganizationAdmin") {
        navigate("/profile/subscription", {
          replace: true,
        });
      } else {
        navigate("/profile", { replace: true });
      }
    }
  }, [groupId, groupRole, navigate]);

  const referralMessage = useMemo(() => {
    if (availableCredit <= 0) return null;

    let discountAmount = 0;
    if (activeCoupon) {
      const percentage = activeCoupon.percentage || activeCoupon.percentageOff / 100 || 0;
      discountAmount = basePrice * percentage;
    }

    if (discountAmount > 0) {
      if (discountAmount >= availableCredit) {
        return "Discounts and referral credit are not stackable. We will apply the higher discount at checkout.";
      } else {
        return "Discounts and referral credit are not stackable. We will apply the higher referral credit at checkout.";
      }
    } else {
      return "We will apply your available referral credit at checkout.";
    }
  }, [availableCredit, activeCoupon, basePrice]);

  return (
    <Box
      sx={{
        ...fillParent,
        ...scrollingBox,
        ...footerFlex,
      }}
    >
      <Helmet>
        <title>Sooth - Profile - Individual Subscription Sign Up</title>
      </Helmet>
      <Stack
        direction="column"
        sx={{
          pr: isMobile ? 0 : "25vw",
        }}
      >
        <PageTitle>
          Individual Plan{" "}
          <FeatureHelpVideo
            videoId="984391120"
            title="Tip: How do I complete this page?"
          />
        </PageTitle>
        <Typography>
          You are purchasing an individual plan for yourself.
        </Typography>
        <Typography>
          Please select the appropriate plan for you from the options below:
        </Typography>
        {checkoutSession == null ? (
          <Fragment>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent={isMobile ? "center" : "space-between"}
              alignItems={isMobile ? "normal" : "center"}
            >
              <Box sx={{ flex: 6 }}>
                <ToggleButtonGroup
                  fullWidth
                  value={gradeLevel}
                  onChange={(event, value) =>
                    handleSelectionChange(value, setGradeLevel)
                  }
                  exclusive
                  sx={{
                    "& > button": {
                      flex: 1,
                    },
                  }}
                >
                  <ToggleButton value="k12">K-12</ToggleButton>
                  <ToggleButton value="highEd">Higher Ed</ToggleButton>
                  <ToggleButton value="gov">Gov</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <Box
                sx={{
                  flex: 3,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <ToggleButtonGroup
                  value={planLength}
                  onChange={(event, value) =>
                    handleSelectionChange(value, setPlanLength)
                  }
                  exclusive
                >
                  <ToggleButton value="yearly">Yearly</ToggleButton>
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              {basePrice && (
                <IndividualPricing
                  base={basePrice}
                  coupon={activeCoupon}
                  planLength={planLength}
                  autoRenew={autoRenew}
                  sx={{
                    flex: 3,
                  }}
                />
              )}
            </Stack>
            {/* <FormGroup
              sx={{
                alignSelf: isMobile ? "flex-start" : "flex-end",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRenew}
                    onChange={(event) =>
                      handleSelectionChange(event.target.checked, setAutoRenew)
                    }
                    size="small"
                  />
                }
                label="Auto-renew my subscription"
              />
            </FormGroup> */}
            {(activeCoupon && promoCode !== "") || (isReferralCode && promoCode !== "") ? (
              <Alert
                action={
                  <Button
                    color="inherit"
                    variant="text"
                    onClick={handleClearCoupon}
                  >
                    CLEAR
                  </Button>
                }
              >
                <AlertTitle>
                  {isReferralCode
                    ? promoCode
                    : find(
                      activeCoupon?.promoCodes,
                      (promo) => promo.code === promoCode.toUpperCase()
                    )?.code}
                </AlertTitle>
                {isReferralCode
                  ? "Referral discount applied"
                  : find(
                    activeCoupon?.promoCodes,
                    (promo) => promo.code === promoCode.toUpperCase()
                  )?.description}
              </Alert>
            ) : (
              <Stack direction="row" alignItems="flex-start">
                <TextField
                  value={promoCode}
                  onChange={(event) => {
                    const value = event.target.value.toUpperCase();
                    setPromoCode(value);
                    if (value === "") {
                      setPromoError(null);
                    }
                  }}
                  fullWidth
                  label="Promo or Referral Code"
                  helperText={promoError}
                  error={promoError != null}
                  onKeyUp={(event) => handleEnterKey(event, handleApplyCoupon)}
                />
                <Button onClick={handleApplyCoupon}>Apply</Button>
              </Stack>
            )}
            {referralMessage && (
              <Box sx={{ display: "flex", mt: 0.5 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {referralMessage}
                </Typography>
              </Box>
            )}
            <Stack direction="row" sx={{ alignSelf: "flex-end" }}>
              <LoadingButton
                variant="contained"
                disabled={subsLoading || activeProduct == null}
                onClick={handleProductSelectionContinue}
                loading={loading}
              >
                Continue
              </LoadingButton>
            </Stack>
          </Fragment>
        ) : (
          <Stack direction={isMobile ? "column" : "row"}>
            <Box
              sx={{
                flex: 1,
                justifySelf: "start",
              }}
            >
              <Button
                startIcon={<ArrowBack />}
                variant="text"
                onClick={handleProductSelectionBack}
              >
                Back
              </Button>
            </Box>
            <Box
              sx={{
                flex: "auto",
                borderRadius: shape.borderRadius / 4,
                overflow: "hidden",
              }}
            >
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret: checkoutSession.clientSecret,
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </Box>
          </Stack>
        )}
      </Stack>
      <Footer sx={{ px: 0 }} />
    </Box>
  );
}
